'use client';

import { createAvatar } from '@usespaceui/squishmoji';
import { useMemo, useState } from 'react';

import { useMediaQuery } from '@/hooks/use-media-query';

import { cn } from '@/lib/utils';

import type { YourImaiPersona } from './your-imai-persona';
import { YourImaiTrackingAvatar } from './your-imai-tracking-avatar';

/**
 * The squishmoji body spans about 200 of its 316-unit canvas; the rest is
 * room for motion. Draw the canvas this much larger so `size` is the blob.
 */
const CANVAS_TO_BODY = 1.58;

type Face = YourImaiPersona['expression'];

/**
 * The member's IMAI as a squishmoji blob, the same avatar family as worker
 * avatars. Animated instances breathe, blink and morph between looks; still
 * ones are plain markup, a pure function of the look, with no render loop.
 */
export function YourImaiAvatar({
  persona,
  size,
  animate = false,
  className,
}: {
  persona: Pick<
    YourImaiPersona,
    'shape' | 'expression' | 'backgroundStyle' | 'colorSeed'
  >;
  size: number;
  animate?: boolean;
  className?: string;
}) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const moving = animate && !reduceMotion;
  const canvasSize = Math.round(size * CANVAS_TO_BODY);
  const { colorSeed, shape, expression, backgroundStyle } = persona;
  // A still avatar crossfades to a new face (IMAI's mood coming or going):
  // the old face stays underneath while the new one fades in on top. The
  // animated one morphs between faces by itself.
  const [faces, setFaces] = useState<{
    current: Face;
    previous: Face | null;
  }>({ current: expression, previous: null });
  if (faces.current !== expression)
    setFaces({
      current: expression,
      previous: moving || reduceMotion ? null : faces.current,
    });
  const stillMarkup = useMemo(
    () =>
      moving
        ? null
        : createAvatar(colorSeed, {
            shape,
            expression,
            backgroundStyle,
            size: canvasSize,
          }),
    [backgroundStyle, canvasSize, colorSeed, expression, moving, shape]
  );
  const previousMarkup = useMemo(
    () =>
      faces.previous === null
        ? null
        : createAvatar(colorSeed, {
            shape,
            expression: faces.previous,
            backgroundStyle,
            size: canvasSize,
          }),
    [backgroundStyle, canvasSize, colorSeed, faces.previous, shape]
  );
  const canvasClass =
    'pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
  return (
    <span
      aria-hidden="true"
      className={cn('relative inline-block shrink-0', className)}
      style={{ width: size, height: size }}
    >
      {stillMarkup === null ? (
        <YourImaiTrackingAvatar
          persona={persona}
          size={canvasSize}
          className={canvasClass}
        />
      ) : (
        <>
          {previousMarkup === null ? null : (
            <span
              className={cn(canvasClass, 'block leading-none')}
              dangerouslySetInnerHTML={{ __html: previousMarkup }}
            />
          )}
          <span
            key={expression}
            onAnimationEnd={() =>
              setFaces((value) => ({ ...value, previous: null }))
            }
            className={cn(
              canvasClass,
              'block leading-none',
              previousMarkup !== null &&
                'animate-in fade-in duration-300 ease-out'
            )}
            dangerouslySetInnerHTML={{ __html: stillMarkup }}
          />
        </>
      )}
    </span>
  );
}
